import { useState, useEffect } from 'react';
import { SettingsDrawer } from 'calc-nav';
import { Cloud } from 'lucide-react';
import { useStore } from '@/store';
import { selectActiveStudy } from '@/store/selectors';
import { ThemeToggle } from '@/components/settings/ThemeToggle';
import { LoadDemoModal } from '@/components/calculator/LoadDemoModal';
import { useAppLock } from '@/components/AppLock';
import { LockMenu } from '@/components/AppLock';

export function TopBar() {
  const activeStudy = useStore(selectActiveStudy);
  const setActiveScreen = useStore((s) => s.setActiveScreen);
  const showLoadDemo = useStore((s) => s.settings.showLoadDemo);
  const autoSave = useStore((s) => s.settings.autoSave);
  const pendingLoadDemo = useStore((s) => s.pendingLoadDemo);
  const consumeLoadDemoRequest = useStore((s) => s.consumeLoadDemoRequest);
  const { lock, isEnabled, enableEncryption } = useAppLock();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);

  // Consume the wizard escape-hatch flag once on mount (Stage 10).
  useEffect(() => {
    if (pendingLoadDemo) {
      consumeLoadDemoRequest();
      setDemoOpen(true);
    }
  }, [pendingLoadDemo, consumeLoadDemoRequest]);

  return (
    <header className="top-bar" role="banner">
      <div className="top-bar-brand">
        <img src="./favicon.svg" alt="" className="brand-logo" width={28} height={28} />
        <span className="brand-name">BioAvail</span>
      </div>
      <div className="top-bar-study" aria-live="polite">
        {activeStudy ? activeStudy.name : 'No active study'}
      </div>
      <div className="top-bar-actions">
        {autoSave ? (
          <span className="autosave-indicator" title="Changes are saved automatically">
            <Cloud size={14} aria-hidden="true" />
            <span className="autosave-label">Autosave</span>
          </span>
        ) : null}
        {showLoadDemo ? (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            title="Load a demo dataset"
            onClick={() => setDemoOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={demoOpen}
          >
            Load Demo
          </button>
        ) : null}
        {isEnabled && lock ? (
          <LockMenu lock={lock} />
        ) : !isEnabled ? (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            title="Enable password protection"
            onClick={enableEncryption}
            aria-label="Enable password protection"
          >
            <ShieldOffIcon />
          </button>
        ) : null}
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          title="Quick settings"
          onClick={() => setSettingsOpen(true)}
          aria-label="Open quick settings"
          aria-haspopup="dialog"
          aria-expanded={settingsOpen}
        >
          <GearIcon />
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          title="PDF export is coming in Phase 2"
          aria-label="Export PDF (disabled — Phase 2)"
          disabled
          aria-disabled="true"
        >
          Export
        </button>
      </div>
      <SettingsDrawer
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        title="Quick Settings"
      >
        <div className="drawer-settings-row">
          <span className="field-label">Theme</span>
          <ThemeToggle />
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => {
            setSettingsOpen(false);
            setActiveScreen('settings');
          }}
        >
          All settings
        </button>
      </SettingsDrawer>
      <LoadDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </header>
  );
}

function GearIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function ShieldOffIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <line x1="4" y1="4" x2="20" y2="20" />
    </svg>
  );
}
